import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    Mail,
    Plus,
    X,
    Loader2,
    Send,
    Eye,
    Star,
    AlertTriangle,
    TrendingDown,
    BarChart3,
    Check
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';

const WeeklyDigestSettings = ({ organizationId, organizationName }) => {
    const { t } = useTranslation();
    const { isSuperAdmin } = useAuth();
    const [enabled, setEnabled] = useState(true);
    const [recipients, setRecipients] = useState([]);
    const [contactEmail, setContactEmail] = useState('');
    const [fallbackUsed, setFallbackUsed] = useState(false);
    const [recipientSource, setRecipientSource] = useState('none');
    const [resolvedOrgName, setResolvedOrgName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [overrideEmail, setOverrideEmail] = useState('');

    useEffect(() => {
        if (organizationId) loadSettings();
    }, [organizationId]);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/organizations/${organizationId}/weekly-digest`);
            setEnabled(res.data.weekly_digest_enabled);
            setRecipients(res.data.weekly_digest_recipients || []);
            setContactEmail(res.data.contact_email || '');
            setFallbackUsed(res.data.fallback_used || false);
            setRecipientSource(res.data.recipient_source || 'none');
            setResolvedOrgName(res.data.organization_name || organizationName || '');
            setHasChanges(false);
        } catch (err) {
            console.error('Failed to load digest settings:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put(`/organizations/${organizationId}/weekly-digest`, {
                enabled,
                recipients
            });
            toast.success(t('weeklyDigest.saved', 'Weekly digest settings saved'));
            setHasChanges(false);
        } catch (err) {
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleAddRecipient = () => {
        const email = newEmail.trim().toLowerCase();
        if (!email || !email.includes('@')) {
            toast.error(t('weeklyDigest.invalidEmail', 'Please enter a valid email address'));
            return;
        }
        if (recipients.includes(email) || email === contactEmail) {
            toast.error(t('weeklyDigest.duplicate', 'This email is already in the list'));
            return;
        }
        setRecipients(prev => [...prev, email]);
        setNewEmail('');
        setHasChanges(true);
    };

    const handleRemoveRecipient = (email) => {
        setRecipients(prev => prev.filter(r => r !== email));
        setHasChanges(true);
    };

    const handleToggle = (val) => {
        setEnabled(val);
        setHasChanges(true);
    };

    const handlePreview = async () => {
        setPreviewLoading(true);
        setPreviewOpen(true);
        try {
            const params = { organization_id: organizationId };
            if (overrideEmail.trim()) params.override_email = overrideEmail.trim();
            const res = await api.get('/review-shield/weekly-digest/preview', { params });
            setPreview(res.data);
        } catch (err) {
            console.error('Preview error:', err?.response?.data || err);
            // Always render a fallback preview — never blank dialog
            setPreview({
                success: false,
                organization_name: resolvedOrgName || organizationName || 'Organization',
                organization_id: organizationId,
                total_feedback: 0,
                average_score: 0,
                low_ratings_count: 0,
                top_category: null,
                recipients: effectiveRecipients,
                fallback_used: false,
                is_override: false,
                has_data: false,
                no_data_reason: err?.response?.status === 401
                    ? t('weeklyDigest.previewAuthError', 'Session expired. Please log in again.')
                    : t('weeklyDigest.previewUnavailable', 'Preview service temporarily unavailable. Please try again.')
            });
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSendNow = async () => {
        setSending(true);
        try {
            const params = { organization_id: organizationId };
            if (overrideEmail.trim()) params.override_email = overrideEmail.trim();
            const res = await api.post('/review-shield/weekly-digest/send', null, { params });
            if (res.data.sent_count > 0) {
                const target = overrideEmail.trim() || t('weeklyDigest.configuredRecipients', 'configured recipients');
                toast.success(t('weeklyDigest.sent', `Weekly digest sent to ${target}`));
                if (overrideEmail.trim()) setOverrideEmail('');
            } else if (res.data.errors?.length) {
                toast.error(`Failed: ${res.data.errors[0]}`);
            } else {
                toast.info(t('weeklyDigest.noData', 'No feedback data to send this week'));
            }
        } catch (err) {
            toast.error('Failed to send digest');
        } finally {
            setSending(false);
        }
    };

    // Resolved recipients = explicitly configured + fallback if applicable
    const effectiveRecipients = recipients.length > 0 ? recipients : (contactEmail ? [contactEmail] : []);

    if (loading) {
        return (
            <Card>
                <CardContent className="pt-4 flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card data-testid="weekly-digest-settings">
            <CardContent className="pt-5 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-emerald-500" />
                        <h3 className="text-base font-semibold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {t('weeklyDigest.title', 'Weekly Digest')}
                        </h3>
                        <Badge variant={enabled ? 'default' : 'secondary'} className={`text-xs ${enabled ? 'bg-emerald-100 text-emerald-700' : ''}`}>
                            {enabled ? t('weeklyDigest.on', 'ON') : t('weeklyDigest.off', 'OFF')}
                        </Badge>
                    </div>
                    {isSuperAdmin() && (
                        <Switch
                            checked={enabled}
                            onCheckedChange={handleToggle}
                            data-testid="weekly-digest-toggle"
                        />
                    )}
                </div>

                <p className="text-sm text-muted-foreground">
                    {t('weeklyDigest.description', 'A weekly email summary of Review Shield feedback sent every Monday.')}
                </p>

                {/* Recipients */}
                <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('weeklyDigest.recipients', 'Recipients')}</Label>
                    <div className="flex flex-wrap gap-2">
                        {/* Explicitly configured digest recipients */}
                        {recipients.map((email, idx) => (
                            <div key={email} className="flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 px-3 py-1.5 rounded-full text-sm border border-emerald-200 dark:border-emerald-800">
                                <Mail className="w-3 h-3" />
                                {email}
                                {isSuperAdmin() && (
                                    <button
                                        onClick={() => handleRemoveRecipient(email)}
                                        className="ml-1 text-muted-foreground hover:text-red-500 transition-colors"
                                        data-testid={`remove-recipient-${email.replace(/[@.]/g, '-')}`}
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Fallback warning: using org contact email */}
                    {recipients.length === 0 && contactEmail && (
                        <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-md border border-amber-200 dark:border-amber-800" data-testid="weekly-digest-fallback-warning">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>
                                {t('weeklyDigest.fallbackWarning', 'Using organization contact email as fallback:')} <strong>{contactEmail}</strong>
                            </span>
                        </div>
                    )}

                    {/* No recipients at all */}
                    {recipients.length === 0 && !contactEmail && (
                        <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-md border border-red-200 dark:border-red-800" data-testid="weekly-digest-no-recipients-warning">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{t('weeklyDigest.noRecipients', 'At least one notification email is required. Add a recipient below.')}</span>
                        </div>
                    )}

                    {/* Add recipient input - Super Admin only */}
                    {isSuperAdmin() && (
                        <div className="flex gap-2 mt-2">
                            <Input
                                type="email"
                                placeholder={t('weeklyDigest.addEmail', 'Add email address...')}
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleAddRecipient()}
                                className="flex-1"
                                data-testid="weekly-digest-add-email"
                            />
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleAddRecipient}
                                disabled={!newEmail.trim()}
                                data-testid="weekly-digest-add-btn"
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                {t('common.add', 'Add')}
                            </Button>
                        </div>
                    )}
                </div>

                {/* Super Admin Override Email */}
                {isSuperAdmin() && (
                    <div className="space-y-1.5" data-testid="weekly-digest-override-section">
                        <Label className="text-sm font-medium">{t('weeklyDigest.overrideLabel', 'Send digest to (optional override)')}</Label>
                        <Input
                            type="email"
                            placeholder={t('weeklyDigest.overridePlaceholder', 'Leave blank to use configured recipients')}
                            value={overrideEmail}
                            onChange={(e) => setOverrideEmail(e.target.value)}
                            className="text-sm"
                            data-testid="weekly-digest-override-email"
                        />
                        <p className="text-xs text-muted-foreground">
                            {t('weeklyDigest.overrideHelper', 'Override applies to this send only and does not change saved recipients.')}
                        </p>
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreview}
                        data-testid="weekly-digest-preview-btn"
                    >
                        <Eye className="w-4 h-4 mr-1" />
                        {t('weeklyDigest.preview', 'Preview')}
                    </Button>
                    {isSuperAdmin() && (
                        <>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleSendNow}
                                disabled={sending || !enabled || effectiveRecipients.length === 0}
                                data-testid="weekly-digest-send-btn"
                            >
                                {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                                {t('weeklyDigest.sendNow', 'Send Now')}
                            </Button>
                            {hasChanges && (
                                <Button
                                    size="sm"
                                    onClick={handleSave}
                                    disabled={saving}
                                    data-testid="weekly-digest-save-btn"
                                >
                                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                                    {t('common.save', 'Save')}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </CardContent>

            {/* Preview Dialog */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Mail className="w-5 h-5 text-emerald-500" />
                            {t('weeklyDigest.previewTitle', 'Weekly Digest Preview')}
                        </DialogTitle>
                        <DialogDescription>
                            {resolvedOrgName || organizationName || 'Organization'} — {t('weeklyDigest.last7Days', 'Last 7 days')}
                        </DialogDescription>
                    </DialogHeader>
                    {previewLoading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                        </div>
                    ) : preview ? (
                        <div className="space-y-4 py-2">
                            {!preview.has_data ? (
                                <div className="text-center py-4">
                                    <p className="text-muted-foreground text-sm" data-testid="preview-no-data-msg">
                                        {preview.no_data_reason || t('weeklyDigest.noDataPreview', 'No feedback for selected organization in the last 7 days.')}
                                    </p>
                                    {preview.total_feedback > 0 && (
                                        <p className="text-sm font-medium mt-2" data-testid="preview-fallback-count">
                                            {t('weeklyDigest.feedbackReceived', 'Feedback received')}: {preview.total_feedback}
                                        </p>
                                    )}
                                    {preview.fallback_used && (
                                        <p className="text-xs text-amber-600 mt-2">
                                            {t('weeklyDigest.fallbackNote', 'Using organization contact email as fallback.')}
                                        </p>
                                    )}
                                    {preview.success === false && preview.error_detail && (
                                        <p className="text-xs text-red-400 mt-2 font-mono">
                                            {preview.error_detail}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-muted/50 rounded-lg p-3 text-center">
                                            <Star className="w-4 h-4 text-amber-500 mx-auto mb-1" />
                                            <div className="text-xl font-bold text-foreground" data-testid="preview-avg-score">
                                                {preview.average_score}<span className="text-xs text-muted-foreground">/5</span>
                                            </div>
                                            <div className="text-xs text-muted-foreground">{t('reviewShield.avgScore', 'Avg. Score')}</div>
                                        </div>
                                        <div className="bg-muted/50 rounded-lg p-3 text-center">
                                            <BarChart3 className="w-4 h-4 text-blue-500 mx-auto mb-1" />
                                            <div className="text-xl font-bold text-foreground" data-testid="preview-total">
                                                {preview.total_feedback}
                                            </div>
                                            <div className="text-xs text-muted-foreground">{t('reviewShield.totalFeedback', 'Total Feedback')}</div>
                                        </div>
                                        <div className="bg-muted/50 rounded-lg p-3 text-center">
                                            <AlertTriangle className="w-4 h-4 text-red-500 mx-auto mb-1" />
                                            <div className="text-xl font-bold text-red-600" data-testid="preview-low">
                                                {preview.low_ratings_count}
                                            </div>
                                            <div className="text-xs text-muted-foreground">{t('reviewShield.lowRatings', 'Low Ratings')}</div>
                                        </div>
                                        <div className="bg-muted/50 rounded-lg p-3 text-center">
                                            <TrendingDown className="w-4 h-4 text-orange-500 mx-auto mb-1" />
                                            <div className="text-xl font-bold text-foreground capitalize" data-testid="preview-category">
                                                {preview.top_category || '-'}
                                            </div>
                                            <div className="text-xs text-muted-foreground">{t('reviewShield.topCategory', 'Top Category')}</div>
                                        </div>
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        <span className="font-medium">{t('weeklyDigest.willSendTo', 'Will send to')}:</span>{' '}
                                        {preview.recipients?.join(', ') || 'No recipients configured'}
                                        {preview.is_override && (
                                            <Badge variant="outline" className="ml-1.5 text-[10px] px-1.5 py-0 text-blue-600 border-blue-300">Override</Badge>
                                        )}
                                        {!preview.is_override && preview.fallback_used && (
                                            <span className="ml-1 text-amber-600">(fallback)</span>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    ) : null}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                            {t('common.close', 'Close')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
};

export default WeeklyDigestSettings;
